import { useEffect, useMemo, useState } from 'react';
import { downloadCsv } from '@/lib/csv';
import { fetchDonationReportEntries, type DonationReportEntry } from '@/services/donations';

function formatMoney(v: number): string {
  return `$${v.toLocaleString('es-CO')}`;
}

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value + 'T00:00:00').toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function monthLabel(month: string): string {
  if (!month) return '';
  const [y, m] = month.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
}

export default function DonationReport() {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(currentMonth);
  const [entries, setEntries] = useState<DonationReportEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchDonationReportEntries()
      .then((list) => {
        if (!cancelled) setEntries(list);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Error al cargar el historial.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const monthRows = useMemo(
    () => entries.filter((e) => (e.donatedAt || '').slice(0, 7) === month),
    [entries, month],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, { name: string; total: number; count: number }>();
    monthRows.forEach((e) => {
      const name = e.entityName || 'Sin entidad';
      const g = map.get(name) || { name, total: 0, count: 0 };
      g.total += e.amount;
      g.count += 1;
      map.set(name, g);
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [monthRows]);

  const totalDonated = monthRows.reduce((s, e) => s + e.amount, 0);

  function handleDownload() {
    downloadCsv(
      `donaciones-${month}`,
      ['Entidad', 'Monto', 'Fecha', 'Nota'],
      monthRows.map((e) => [e.entityName || 'Sin entidad', e.amount, formatDate(e.donatedAt), e.notes || '']),
    );
  }

  return (
    <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-background-100 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center">
            <i className="ri-hand-heart-line text-accent-600"></i>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground-900">Reporte Mensual</h2>
            <p className="text-xs text-foreground-500">Desglose de donaciones por mes y entidad</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="text-sm border border-background-300 rounded-lg px-3 py-2 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
          />
          <button
            onClick={handleDownload}
            disabled={monthRows.length === 0}
            className="text-sm px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50 whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-download-2-line mr-1.5"></i>Descargar CSV
          </button>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{error}</span>
          </div>
        )}

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-background-50 rounded-lg p-4 border border-background-100">
            <p className="text-xs text-foreground-500">Total donado del mes</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{formatMoney(totalDonated)}</p>
          </div>
          <div className="bg-background-50 rounded-lg p-4 border border-background-100">
            <p className="text-xs text-foreground-500">Entidades beneficiadas</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{grouped.length}</p>
          </div>
          <div className="bg-background-50 rounded-lg p-4 border border-background-100">
            <p className="text-xs text-foreground-500">Aportes</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{monthRows.length}</p>
          </div>
        </div>

        {/* Grouped by entity */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-2">
            Desglose por entidad · {monthLabel(month)}
          </h3>
          {loading ? (
            <div className="text-center py-8 text-foreground-400 text-sm">
              <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando...
            </div>
          ) : grouped.length === 0 ? (
            <div className="text-center py-8 text-foreground-400 text-sm">
              No hay donaciones registradas en este mes.
            </div>
          ) : (
            <div className="border border-background-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-100 text-left bg-background-50">
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Entidad</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-right">Aportes</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-right">Total donado</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped.map((g) => (
                    <tr key={g.name} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-4 py-2.5 text-foreground-900 font-medium">{g.name}</td>
                      <td className="px-4 py-2.5 text-right text-foreground-600">{g.count}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-foreground-950">{formatMoney(g.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* History list */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-2">
            Historial del mes ({monthRows.length})
          </h3>
          {loading ? (
            <div className="text-center py-8 text-foreground-400 text-sm">
              <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando...
            </div>
          ) : monthRows.length === 0 ? (
            <div className="text-center py-8 text-foreground-400 text-sm">
              Selecciona un mes con donaciones para ver el detalle.
            </div>
          ) : (
            <div className="border border-background-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-100 text-left bg-background-50">
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Entidad</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-right">Monto</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Fecha</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {monthRows.map((e) => (
                    <tr key={e.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-4 py-2.5 text-foreground-900">{e.entityName || 'Sin entidad'}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-foreground-950">{formatMoney(e.amount)}</td>
                      <td className="px-4 py-2.5 text-foreground-500 whitespace-nowrap">{formatDate(e.donatedAt)}</td>
                      <td className="px-4 py-2.5 text-foreground-500">{e.notes || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}